/**
 * G2eGente - Feed de Comunicados para Candidatos
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { newsFeedAPI, API_URL } from '../../lib/api';
import { ReactionBar } from '../../components/news/reactions';
import { NewsPostHeader } from '../../components/news/NewsPostHeader';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  ArrowLeft, 
  Loader2, 
  Newspaper, 
  AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';

const TIPO_CONFIG = {
  noticia: { label: 'Notícia', color: 'bg-blue-100 text-blue-700' },
  comunicado: { label: 'Comunicado', color: 'bg-green-100 text-green-700' },
  alerta: { label: 'Alerta', color: 'bg-red-100 text-red-700' },
  treinamento: { label: 'Treinamento', color: 'bg-purple-100 text-purple-700' }
};

const PRIORIDADE_CONFIG = {
  normal: { label: 'Normal', color: 'bg-slate-100 text-slate-700' },
  alta: { label: 'Alta', color: 'bg-amber-100 text-amber-700' },
  urgente: { label: 'Urgente', color: 'bg-red-100 text-red-700' }
};

export default function CandidateNewsFeed() {
  const navigate = useNavigate();
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, unread

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      const response = await newsFeedAPI.getCandidateFeed();
      setNews(response.data.news || []);
    } catch (error) {
      toast.error('Erro ao carregar comunicados');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenNews = async (item) => {
    // Marcar como lido
    if (!item.lido) {
      try {
        await newsFeedAPI.markCandidateAsRead(item.id);
        setNews(prev => prev.map(n => 
          n.id === item.id ? { ...n, lido: true } : n
        ));
      } catch (error) {
        console.error('Erro ao marcar como lido:', error);
      }
    }
    // Navegar para detalhe
    navigate(`/m/feed/${item.id}`);
  };

  const filteredNews = filter === 'unread' 
    ? news.filter(n => !n.lido)
    : news;

  const unreadCount = news.filter(n => !n.lido).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="candidate-news-feed">
      {/* Header */}
      <header className="bg-gradient-to-r from-cyan-600 to-cyan-700 text-white p-4 pt-8">
        <div className="flex items-center gap-3 mb-4">
          <button 
            onClick={() => navigate(-1)}
            className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-xl font-bold">Feed de Notícias</h1>
            <p className="text-cyan-100 text-sm">
              {unreadCount > 0 ? `${unreadCount} não lido(s)` : 'Todos lidos'}
            </p>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              filter === 'all' 
                ? 'bg-white text-cyan-700' 
                : 'bg-white/20 text-white'
            }`}
          >
            Todos ({news.length})
          </button>
          <button
            onClick={() => setFilter('unread')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              filter === 'unread' 
                ? 'bg-white text-cyan-700' 
                : 'bg-white/20 text-white'
            }`}
          >
            Não Lidos ({unreadCount})
          </button>
        </div>
      </header>

      {/* Content */}
      <main className="p-4">
        {filteredNews.length === 0 ? (
          <div className="text-center py-12">
            <Newspaper className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-700">
              {filter === 'unread' ? 'Todos os comunicados foram lidos' : 'Nenhum comunicado disponível'}
            </h3>
            <p className="text-slate-500 mt-2">
              {filter === 'unread' 
                ? 'Você está em dia!' 
                : 'Novos comunicados aparecerão aqui'}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredNews.map(item => (
              <Card 
                key={item.id}
                data-testid={`candidate-news-card-${item.id}`}
                className={`border overflow-hidden cursor-pointer hover:shadow-md transition-shadow ${
                  !item.lido ? 'border-cyan-200 bg-cyan-50/50' : 'border-slate-200'
                }`}
                onClick={() => handleOpenNews(item)}
              >
                <div className="px-4 pt-3 pb-3">
                  <NewsPostHeader
                    news={item}
                    tipoLabel={TIPO_CONFIG[item.tipo]?.label || item.tipo}
                    unread={!item.lido}
                    testId={`candidate-news-header-${item.id}`}
                  />
                </div>
                {item.imagem_capa && (
                  <div className="w-full aspect-video bg-slate-100">
                    <img 
                      src={item.imagem_capa.startsWith('/') ? `${API_URL}${item.imagem_capa}` : item.imagem_capa} 
                      alt={item.titulo} 
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </div>
                )}
                <CardContent className="p-4">
                  {item.prioridade !== 'normal' && (
                    <Badge className={`mb-2 ${PRIORIDADE_CONFIG[item.prioridade]?.color}`}>
                      {item.prioridade === 'urgente' && <AlertTriangle className="w-3 h-3 mr-1" />}
                      {PRIORIDADE_CONFIG[item.prioridade]?.label}
                    </Badge>
                  )}
                  <h3 className={`font-semibold ${!item.lido ? 'text-slate-900' : 'text-slate-700'}`}>
                    {item.titulo}
                  </h3>
                  <div className="mt-3 pt-2 border-t border-slate-100">
                    <ReactionBar
                      news={item}
                      reactFn={newsFeedAPI.reactCandidate}
                      onChange={(id, data) => setNews(prev => prev.map(n => n.id === id ? { ...n, ...data } : n))}
                    />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
